import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Skeleton,
  Alert,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui'
import { RefreshCw, ExternalLink } from 'lucide-react'
import { useApiClient, api, type AuthorizedApp } from '@/api'

export function AppsPage() {
  const client = useApiClient()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [apps, setApps] = useState<AuthorizedApp[]>([])
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.getApps(client)
      setApps(response.apps)
    } catch (err) {
      console.error('Failed to load apps:', err)
      setError('Failed to load connected apps')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleDisconnect = async (appSlug: string) => {
    setActionLoading(appSlug)
    try {
      await api.revokeApp(client, appSlug)
      setApps((prev) => prev.filter((a) => a.app.slug !== appSlug))
    } catch (err) {
      console.error('Failed to disconnect app:', err)
      setError('Failed to disconnect app')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Apps"
          description="Apps you've connected to your account"
        />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-12 w-12 rounded-lg" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-32 mb-2" />
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error && apps.length === 0) {
    return (
      <div>
        <PageHeader
          title="Apps"
          description="Apps you've connected to your account"
        />
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-[var(--text-secondary)] mb-4">{error}</p>
            <Button onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Apps"
        description="Apps you've connected to your account"
      />

      {error && (
        <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {apps.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-[var(--text-secondary)]">
              No apps connected yet
            </p>
            <p className="text-sm text-[var(--text-muted)] mt-2">
              When you authorize apps to access your account, they will appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {apps.map((authorizedApp) => (
            <Card key={authorizedApp.app_user_id}>
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  {/* Logo */}
                  <div className="h-12 w-12 rounded-lg bg-[var(--bg-elevated-2)] flex items-center justify-center shrink-0">
                    {authorizedApp.app.logo_url ? (
                      <img
                        src={authorizedApp.app.logo_url}
                        alt={authorizedApp.app.name}
                        className="h-8 w-8 object-contain"
                      />
                    ) : (
                      <span className="text-lg font-medium text-[var(--text-secondary)]">
                        {authorizedApp.app.name[0]}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium text-[var(--text-primary)]">
                        {authorizedApp.app.name}
                      </h3>
                      {authorizedApp.app.homepage_url && (
                        <a
                          href={authorizedApp.app.homepage_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-sm text-[var(--text-secondary)]">
                      {authorizedApp.scopes.length > 0 ? (
                        <span>Access: {authorizedApp.scopes.join(', ')}</span>
                      ) : (
                        <span>Basic access</span>
                      )}
                      <span>Connected {formatDate(authorizedApp.authorized_at)}</span>
                    </div>
                    {authorizedApp.last_used_at && (
                      <p className="mt-1 text-xs text-[var(--text-muted)]">
                        Last used {formatTimeAgo(authorizedApp.last_used_at)}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" asChild>
                      <Link to={`/apps/${authorizedApp.app.slug}`}>
                        View details
                      </Link>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={actionLoading === authorizedApp.app.slug}
                        >
                          Disconnect
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Disconnect {authorizedApp.app.name}?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This will revoke {authorizedApp.app.name}'s access to your account.
                            You can reconnect at any time.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDisconnect(authorizedApp.app.slug)}
                          >
                            Disconnect
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  })
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}
